import React, { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { useSnackbar } from '../../../../../../../../../../shared/components/Snackbar/context/SnackBar.context.container'

import Button from '../../../../../../../../../../shared/components/Button/Button.component'
import Modal from '../../../../../../../../../../shared/components/Modal/Modal.component'

import { reassignItemRequestForAdmin } from '../../../../../../../../../../redux/states/admin/orders/reassignItemRequestForAdmin/reassignItemRequestForAdmin.slice'
import { RootState } from '../../../../../../../../../../redux/store/store.types'
import status from '../../../../../../../../../../shared/functions/Status/status.dictionaries'
import { useAppDispatch } from '../../../../../../../../../../shared/hooks/redux.hooks'
import styles from './ReleaseItemButton.styles'
import { ReleaseItemButtonProps } from './ReleaseItemButton.types'

const ReleaseItemButton: React.FC<ReleaseItemButtonProps> = (props) => {
  const { itemRequestId, itemRequest, onSuccess } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { show } = useSnackbar()

  const { loading: releaseLoading, success: releaseItemSuccess } = useSelector(
    (state: RootState) => state.reassignItemRequestForAdmin
  )

  // Check if flight is booked, item is purchased, or item is not accepted by traveler
  const isFlightBooked = itemRequest?.offerRequest?.flightItinerary?.status?.name === 'booked'
  const isItemPurchased = itemRequest?.status?.name === 'purchased'

  // Check if item request is already released/reassigned
  const isItemReleased = useMemo(() => {
    // If we have a successful release for this specific item request, use that status
    if (releaseItemSuccess?.data?.itemRequestId === itemRequestId) {
      return releaseItemSuccess.data.reassignmentStatus === 'successful'
    }

    // Otherwise, check if the item request is in a released state
    return (
      itemRequest?.status?.name === status.itemRequestStatus.reassignedByShopper ||
      itemRequest?.status?.name === 'cancelled'
    )
  }, [itemRequest?.status?.name, releaseItemSuccess?.data, itemRequestId])

  const getButtonText = () => {
    if (isItemReleased) {
      return 'Item Released'
    }

    return 'Release Item'
  }

  const getTooltipText = () => {
    if (isFlightBooked) {
      return 'Cannot release item when flight is booked'
    }

    if (isItemPurchased) {
      return 'Cannot release item when already purchased'
    }

    if (isItemReleased) {
      return 'Item has been released'
    }

    return undefined
  }

  const isButtonDisabled = isFlightBooked || isItemPurchased || isItemReleased

  const handleReleaseClick = () => {
    if (!isButtonDisabled) {
      setIsModalOpen(true)
    }
  }

  const handleConfirmRelease = () => {
    dispatch(
      reassignItemRequestForAdmin({
        request: { itemRequestId },
        onSuccess: () => {
          setIsModalOpen(false)
          show({ message: 'Item released successfully.', severity: 'success' })
          onSuccess && onSuccess()
        }
      })
    )
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        text={getButtonText()}
        buttonType="error"
        disabled={isButtonDisabled}
        onClick={handleReleaseClick}
        tooltipText={getTooltipText()}
        style={styles.button}
      />

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Release Item"
        primaryButton={{
          label: 'Confirm',
          onClick: handleConfirmRelease,
          loading: releaseLoading
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: handleCloseModal
        }}
        body="Are you sure you want to release this item? This will move the item request to a new offer request and make it available for other travelers."
      />
    </>
  )
}

export default ReleaseItemButton
